import { z } from "zod"

import { TEXT_CONTENT_TYPE } from "@tfg/types/constants/communication-studio.constant"
import { ProductSheetLanguage } from "@tfg/types/constants/language.constants"
import { ProductSheetLanguageSchema } from "@tfg/types/schemas/languages"
import { ComponentTypesEnum } from "@tfg/types/types/communication-studio/components.types"

export const COMPONENT_TYPES = z.nativeEnum(ComponentTypesEnum)

export const COMPONENT_CATEGORIES = {
  GENERIC_PASSPORT_TITLE: [ComponentTypesEnum.GENERIC_PASSPORT_TITLE],
  TEXT: [ComponentTypesEnum.TEXT_HEADER, ComponentTypesEnum.TEXT_PARAGRAPH],
  ACTIVITY: [
    ComponentTypesEnum.ACTIVITY_LIST,
    ComponentTypesEnum.ACTIVITY_LIST_WITH_ICONS,
  ],
  HEADER: [
    ComponentTypesEnum.HEADER_WITH_PRODUCT,
    ComponentTypesEnum.HEADER_WITH_PRODUCT_NAME,
  ],
  FOOTER: [
    ComponentTypesEnum.TRIMAN_FOOTER,
    ComponentTypesEnum.TRIMAN_FOOTER_POWERED_BY,
  ],
  PRODUCT_INFO: [
    ComponentTypesEnum.RECYCLED_MATERIALS_INFO,
    ComponentTypesEnum.PACKAGING_INFO,
    ComponentTypesEnum.ECOSCORE,
  ],
} as const

export type ComponentType =
  (typeof ComponentTypesEnum)[keyof typeof ComponentTypesEnum]
export const TextContentJSONSchema = z.object({
  text: z.string(),
  type: z.nativeEnum(TEXT_CONTENT_TYPE),
  link: z.string().optional(),
})

// Component Content
export type TextContentJSON = z.infer<typeof TextContentJSONSchema>

export type ContentBlock = z.infer<typeof ContentBlockSchema>
export type Content = Partial<{
  [key in ProductSheetLanguage]: ContentBlock[]
}>

export type ProductSheetComponent = z.infer<typeof ProductSheetComponentSchema>

export const TEXT_COMPONENTS = [ComponentTypesEnum.TEXT_PARAGRAPH]

export const FormattingSchema = z.array(
  z.object({
    type: z.nativeEnum(TEXT_CONTENT_TYPE),
    start: z.number(),
    end: z.number(),
    link: z.string().optional(),
  })
)
export type Formatting = z.infer<typeof FormattingSchema>
export const FormattedTextContentSchema = z.object({
  text: z.string(),
  tag: z.enum(["p", "h3"]),
  formatting: FormattingSchema.optional(),
})
export type FormattedTextContent = z.infer<typeof FormattedTextContentSchema>

export const BulletedListComponent = z.object({
  componentType: z.literal(ComponentTypesEnum.BULLETED_LIST),
  order: z.number(),
  content: z.object({
    items: z.array(FormattedTextContentSchema),
    style: z.enum(["disc", "circle", "square"]),
  }),
})

export const BulletedListContentSchema = z.object({
  items: z.array(FormattedTextContentSchema),
  style: z.enum(["disc", "circle", "square"]),
})

export const ContentBlockSchema = z.object({
  type: z.nativeEnum(ComponentTypesEnum),
  textContent: z
    .array(TextContentJSONSchema)
    .or(BulletedListContentSchema)
    .or(z.array(FormattedTextContentSchema)),
})

export const ComponentContentSchema = z.record(
  ProductSheetLanguageSchema,
  z.array(ContentBlockSchema)
)

export type ComponentContent = Partial<
  Record<Partial<ProductSheetLanguage>, z.infer<typeof ContentBlockSchema>[]>
>
export const ProductSheetComponentSchema = z.object({
  id: z.string(),
  componentType: z.nativeEnum(ComponentTypesEnum).transform((val) => {
    return val as ComponentType
  }),
  content: ComponentContentSchema,
  order: z.number(),
})

export const ContentWithTextContentSchema = z.object({
  type: z.nativeEnum(ComponentTypesEnum),
  textContent: z.array(TextContentJSONSchema).nullable(),
})
export type ContentWithTextContent = z.infer<
  typeof ContentWithTextContentSchema
>

export const ContentBlockWithTextContent = z.record(
  ProductSheetLanguageSchema,
  z.array(ContentWithTextContentSchema)
)
export type ContentBlockWithTextContent = z.infer<
  typeof ContentBlockWithTextContent
>
